import { RefObject, useEffect, useMemo, useState } from 'react';
import { Box, Paper, TableContainer, TablePagination } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserResponse } from 'layouts/pages/System/users/services/responses/user.response';
import { LeadStatusResponse } from '@layouts/pages/Settings/general/lead-status/services/responses/lead-status.response';
import { BoardColumnResponse } from '../../services/responses/board-column.response';
import { LeadResponse } from '../../services/responses/lead.response';
import { ScheduleFilter } from '../../filters/crm-schedule-filter';
import { buildLeadListQuery } from '../../filters/crm-lead-list-query';
import { CrmLeadListService } from '../../services/crm-lead-list.service';
import { CrmLeadListHook } from '../../hooks/crm-lead-list.hook';
import { useNarrowerThan } from '../../hooks/use-narrower-than.hook';
import { LeadAssignMenu, LeadStatusMenu } from '../shared/crm-lead-menus';
import { useProfilePermissions } from '@hooks/use-profile-permissions.hook';
import { assignableUsers } from '../../filters/crm-assignable-users';
import { CrmLeadListTable, NARROW_WIDTH, SortDirection, firstSortDirection } from './crm-lead-list-table';
import { TABLE_CONTAINER_SX, paperSx } from './crm-lead-list.styles';

interface Props {
    boardId: string;
    columns: BoardColumnResponse[];
    searchTerm: string;
    statusFilterId: string | null;
    scheduleFilter: ScheduleFilter | null;
    ownerFilter: string | null;
    tagFilter: string | null;
    showClosed: boolean;
    refresh: boolean;
    /// O mesmo alvo de medição das colunas: a lista ocupa a altura que sobra e o corpo da tabela rola.
    containerRef: RefObject<HTMLDivElement>;
    height: number | null;
    onOpenLead: (lead: LeadResponse) => void;
    /// Quadro arquivado não muda status nem dono, como no card.
    boardActive: boolean;
    statuses: LeadStatusResponse[];
    users: UserResponse[];
    onChangeStatus: (lead: LeadResponse, statusId: string) => void;
    onAssign: (lead: LeadResponse, userId: string | null) => void;
}

const SEARCH_DEBOUNCE_MS = 300;

/// Um menu por vez, preso à célula clicada.
interface OpenMenu {
    kind: 'status' | 'assign';
    lead: LeadResponse;
    anchor: HTMLElement;
}

export function CrmLeadList({
    boardId, columns, searchTerm, statusFilterId, scheduleFilter, ownerFilter, tagFilter, showClosed, refresh,
    containerRef, height, onOpenLead, boardActive, statuses, users, onChangeStatus, onAssign,
}: Props) {
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('UpdatedAt');
    const [orderDirection, setOrderDirection] = useState<SortDirection>('desc');
    const [debouncedSearch, setDebouncedSearch] = useState(searchTerm.trim());
    const { ref: tableAreaRef, narrow } = useNarrowerThan<HTMLDivElement>(NARROW_WIDTH);
    const [menu, setMenu] = useState<OpenMenu | null>(null);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setDebouncedSearch('');
            return undefined;
        }

        const timer = setTimeout(() => setDebouncedSearch(searchTerm), SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Apagar a busca vale na hora: esperar o atraso faria "Limpar filtros" buscar duas vezes.
    const search = searchTerm.trim() ? debouncedSearch : '';

    // O agora entra só quando um filtro muda; senão a querystring mudaria e buscaria sem parar.
    const filter = useMemo(
        () => buildLeadListQuery({
            boardId,
            search,
            statusId: statusFilterId,
            ownerFilter,
            tagFilter,
            includeClosed: showClosed,
            scheduleFilter,
            now: new Date(),
        }),
        [boardId, search, statusFilterId, ownerFilter, tagFilter, showClosed, scheduleFilter]
    );

    // Filtro novo volta à primeira página no mesmo render: num efeito, a busca sairia duas vezes.
    const [pagedFilter, setPagedFilter] = useState(filter);

    if (pagedFilter !== filter) {
        setPagedFilter(filter);
        setPage(0);
    }

    const { json } = new CrmLeadListHook({
        page,
        limit: rowsPerPage,
        orderBy,
        orderDirection,
        filter,
        refresh,
        service: new CrmLeadListService(),
        enabled: Boolean(boardId),
    }).getJson();

    const changeOrder = (field: string) => {
        if (field === orderBy) {
            setOrderDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setOrderBy(field);
        setOrderDirection(firstSortDirection(field));
    };

    const stageNames = useMemo(
        () => new Map(columns.map((column) => [column.id, column.name])),
        [columns]
    );

    const openStatusMenu = boardActive && statuses.length > 0
        ? (lead: LeadResponse, anchor: HTMLElement) => setMenu({ kind: 'status', lead, anchor })
        : undefined;
    const { canAssignLeads, userId } = useProfilePermissions();
    // Sem a ação, a linha de lead com dono não abre menu: não haveria ninguém para escolher.
    const openAssignMenu = boardActive && users.length > 0
        ? (lead: LeadResponse, anchor: HTMLElement) => {
            if (assignableUsers(users, lead.assignedUserId, canAssignLeads, userId).length > 0)
                setMenu({ kind: 'assign', lead, anchor });
        }
        : undefined;

    return (
        <Box ref={containerRef} sx={{ pb: 2 }}>
            <Paper ref={tableAreaRef} sx={paperSx(height)}>
                <TableContainer sx={TABLE_CONTAINER_SX}>
                    <CrmLeadListTable
                        leads={json?.data?.records ?? []}
                        loaded={Boolean(json)}
                        compact={narrow}
                        stageNames={stageNames}
                        orderBy={orderBy}
                        orderDirection={orderDirection}
                        onSort={changeOrder}
                        onOpen={onOpenLead}
                        onOpenStatusMenu={openStatusMenu}
                        onOpenAssignMenu={openAssignMenu}
                    />
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage={t('general.linhasPorPagina')}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('general.X-de-X')} ${count !== -1 ? count : `${t('general.maisQue')} ${to}`}`
                    }
                    component="div"
                    count={json?.data?.totalItens ?? 0}
                    page={page}
                    onPageChange={(_, next) => setPage(next)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value));
                        setPage(0);
                    }}
                    sx={{ flexShrink: 0 }}
                />
            </Paper>

            {menu?.kind === 'status' && (
                <LeadStatusMenu
                    lead={menu.lead}
                    statuses={statuses}
                    anchorEl={menu.anchor}
                    onClose={() => setMenu(null)}
                    onSelect={(statusId) => onChangeStatus(menu.lead, statusId)}
                />
            )}

            {menu?.kind === 'assign' && (
                <LeadAssignMenu
                    lead={menu.lead}
                    users={users}
                    anchorEl={menu.anchor}
                    onClose={() => setMenu(null)}
                    onSelect={(userId) => onAssign(menu.lead, userId)}
                />
            )}
        </Box>
    );
}
